import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useApplicationContext } from 'ApplicationContext';

interface DeviceLicensePublicKeyDialogProps {
  open: boolean;
  onClose: () => void;
}

export const DeviceLicensePublicKeyDialog: React.FC<DeviceLicensePublicKeyDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const context = useApplicationContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [publicKey, setPublicKey] = useState<string>('');

  useEffect(() => {
    if (!open) {
      return;
    }

    const loadKey = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await context.api.api.licenseGetLicensePublicKey({
          format: 'text',
        });
        const responseData = response.data as unknown;
        if (typeof responseData === 'string') {
          setPublicKey(responseData);
        } else if (responseData instanceof Blob) {
          setPublicKey(await responseData.text());
        } else {
          setPublicKey('');
        }
      } catch (err) {
        setError(t('error.fetchFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadKey();
  }, [context.api.api, open, t]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleCopy = async () => {
    if (!publicKey) {
      return;
    }
    try {
      await navigator.clipboard.writeText(publicKey);
    } catch (err) {
      setError(t('error.fetchFailed'));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{t('deviceManagement.publicKey.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('deviceManagement.publicKey.label')}
            value={publicKey}
            multiline
            minRows={6}
            fullWidth
            InputProps={{ readOnly: true }}
            placeholder={loading ? t('loading') : t('deviceManagement.publicKey.empty')}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCopy} color="primary" disabled={!publicKey || loading}>
          {t('deviceManagement.publicKey.copy')}
        </Button>
        <Button onClick={handleClose} color="secondary">
          {t('deviceManagement.publicKey.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
